import { useState } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, MessageSquareHeart, Send, Loader2, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";

export default function ProfileFeedback() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSendFeedback = async () => {
    if (!message.trim()) {
      toast.error("Escreve alguma coisa aí! Nem que seja um 'oi'.");
      return;
    }

    if (message.trim().length < 10) {
      toast.error("Conta mais! Feedback de uma palavra não ajuda muito. 😅");
      return;
    }

    setIsSending(true);

    try {
      await addDoc(collection(db, "feedbacks"), {
        userId: user?.uid || "anonymous",
        email: user?.email || "anonymous",
        message: message.trim(),
        timestamp: serverTimestamp(),
        status: "pending",
        source: "app_profile"
      });

      toast.success("Valeu pelo feedback! Prometemos ler com carinho (e sem chorar). ❤️");
      navigate("/profile");
    } catch (error) {
      toast.error("Ops! O porquinho entalou. Tenta de novo?");
    }

    setIsSending(false);
  };

  return (
    <div className="min-h-screen bg-[#fafbfc] pb-32">
      <div className="max-w-lg mx-auto px-5 py-6">
        
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Link to="/profile">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">
              Deixe um Feedback
            </h1>
            <p className="text-slate-500 text-sm">
              Elogios, críticas ou ideias malucas. Tudo vale!
            </p>
          </div>
        </motion.header>

        {/* Illustration Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-[24px] p-6 mb-6 shadow-lg shadow-pink-500/20"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <MessageSquareHeart className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Sua opinião importa!</h2>
              <p className="text-white/80 text-sm">
                A gente lê tudo. Sério mesmo.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Feedback Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100"
        >
          <label className="text-sm font-semibold text-slate-700 mb-3 block">
            O que você gostaria de compartilhar?
          </label>
          
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Pode desabafar... O app não vai se ofender. 😊"
            className="min-h-[150px] rounded-xl bg-slate-50 border-slate-200 resize-none"
            maxLength={1000}
            data-testid="feedback-textarea"
          />
          
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-slate-400">
              {message.length}/1000 caracteres
            </p>
            {message.length >= 10 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-1 text-emerald-500 text-xs"
              >
                <Sparkles className="w-3 h-3" />
                Perfeito!
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Send Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6"
        >
          <Button
            className="w-full h-14 rounded-full font-bold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-lg shadow-pink-500/30"
            onClick={handleSendFeedback}
            disabled={isSending || !message.trim()}
            data-testid="send-feedback"
          >
            {isSending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Enviar feedback
              </>
            )}
          </Button>
        </motion.div>

        {/* Suggestions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Sugestões de tópicos
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              "Adorei o app!",
              "Encontrei um bug",
              "Tenho uma ideia",
              "Poderia melhorar...",
              "Quero uma feature"
            ].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setMessage(suggestion + " ")}
                className="px-3 py-1.5 bg-white rounded-full text-xs font-medium text-slate-600 border border-slate-200 hover:border-pink-300 hover:bg-pink-50 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Fun fact */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-xs text-slate-400 mt-8 italic"
        >
          "Crítica construtiva é como vitamina: faz bem, mesmo com gosto estranho."
        </motion.p>
      </div>
    </div>
  );
}
